import { logger } from "utils/logger"

export const fileToBase64 = (file: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

export const getImageData = (
  data: string,
  onLoad: (info: { width: number; height: number }) => void
) => {
  const img = new Image()

  img.onload = () => {
    onLoad({
      width: img.width,
      height: img.height,
    })
  }

  img.src = data
}

export const getImageFromFile = (
  file: Blob,
  onload: (info: { src: string; width: number; height: number }) => void
) => {
  const reader = new FileReader()
  reader.readAsDataURL(file)
  reader.onload = () => {
    const result = reader.result
    if (typeof result !== "string") return

    const img = new Image()

    img.onload = () => {
      onload({
        src: result,
        width: img.width,
        height: img.height,
      })
    }

    img.src = result
  }
  reader.onerror = function (error) {
    logger.error("files", "FileReader failed", error)
  }
}

// Centers the image on a transparent canvas of the target aspect ratio (never crops).
// The widget renders the logo height-locked with no object-fit, so the stored asset must
// already be the right ratio — padding (not cropping) keeps the whole logomark intact.
export const padImageToRatio = (
  src: string,
  targetRatio: number
): Promise<{ src: string; width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const w = img.naturalWidth
      const h = img.naturalHeight
      // Already matches the target ratio — keep the original bytes untouched.
      if (Math.abs(w / h - targetRatio) <= 0.01) {
        resolve({ src, width: w, height: h })
        return
      }
      const tooNarrow = w / h < targetRatio
      const canvasW = tooNarrow ? Math.round(h * targetRatio) : w
      const canvasH = tooNarrow ? h : Math.round(w / targetRatio)
      const canvas = document.createElement("canvas")
      canvas.width = canvasW
      canvas.height = canvasH
      const ctx = canvas.getContext("2d")
      if (!ctx) {
        resolve({ src, width: w, height: h })
        return
      }
      ctx.drawImage(img, Math.round((canvasW - w) / 2), Math.round((canvasH - h) / 2), w, h)
      resolve({ src: canvas.toDataURL("image/png"), width: canvasW, height: canvasH })
    }
    img.onerror = reject
    img.src = src
  })

export const getFileInfo = (url: string) => {
  const parsedUrl = new URL(url)
  const path = parsedUrl.pathname
  const fileNameWithExtension = path.split("/").pop() ?? ""
  const fileParts = fileNameWithExtension.split(".")

  const type = fileParts.pop()
  const name = fileParts.join(".")

  return {
    name,
    type,
  }
}
